// A lecture's linked content (videos, vignettes, flashcards) loaded into the
// videos, vignettes and flashcards slices (ora-fe ADR-0002: the requests live
// in `src/services/lectures.ts`).
import {
  getLectureVideos,
  postBulkLectureVideos,
  postLectureFlashcards,
  postLectureVignettes,
  type LectureContentFetchOptions,
  type SuspendedContentIds,
} from '../../services/lectures';
import type { FlashcardsState } from '../../types/Flashcard';
import type { Video } from '../../types/Video';
import type { Vignette } from '../../types/Vignette';
import { flashcardsLoaded } from '../flashcards/flashcardsSlice';
import {
  bulkVideosLoaded,
  bulkVideosLoading,
  videosLoaded,
  videosLoading,
} from '../videos/videosSlice';
import {
  setVignettes,
  vignettesFailed,
  vignettesLoading,
} from '../vignettes/vignettesSlice';
import type { LectureThunk } from './lectureThunks';

// Get Videos for a Specific Lecture
export const fetchLectureVideos =
  (lectureId: string): LectureThunk<Video[]> =>
  async (dispatch) => {
    dispatch(videosLoading(lectureId));

    const videos = await getLectureVideos(lectureId);

    dispatch(videosLoaded({ lectureId, videos }));

    return videos;
  };

/**
 * Fetch vignettes for multiple lectures
 * @param lectureIds - Array of lecture IDs
 * @param options - Pass `{ includeSuspended: true }` to also receive the ids
 * of the suspended vignettes the response body omits.
 */
export function fetchLectureVignettes(
  lectureIds: string[]
): LectureThunk<Vignette[]>;
export function fetchLectureVignettes(
  lectureIds: string[],
  options: LectureContentFetchOptions & { includeSuspended: true }
): LectureThunk<SuspendedContentIds & { vignettes: Vignette[] }>;
export function fetchLectureVignettes(
  lectureIds: string[],
  options?: LectureContentFetchOptions
): LectureThunk<
  Vignette[] | (SuspendedContentIds & { vignettes: Vignette[] })
> {
  return async (dispatch) => {
    try {
      dispatch(vignettesLoading());

      const { vignettes, suspendedIds } = await postLectureVignettes(
        lectureIds,
        options
      );

      dispatch(setVignettes(vignettes));

      if (!options?.includeSuspended) return vignettes;

      return { vignettes, suspendedIds };
    } catch (error) {
      console.error('Error fetching lecture vignettes:', error);

      dispatch(
        vignettesFailed(
          error instanceof Error ? error.message : 'Failed to fetch vignettes'
        )
      );

      throw error;
    }
  };
}

/**
 * Fetch flashcards for multiple lectures
 * @param lectureIds - Array of lecture IDs
 * @param options - Pass `{ includeSuspended: true }` to also receive the ids
 * of the suspended flashcards the response body omits.
 */
export function fetchLectureFlashcards(
  lectureIds: string[]
): LectureThunk<FlashcardsState>;
export function fetchLectureFlashcards(
  lectureIds: string[],
  options: LectureContentFetchOptions & { includeSuspended: true }
): LectureThunk<SuspendedContentIds & { flashcards: FlashcardsState }>;
export function fetchLectureFlashcards(
  lectureIds: string[],
  options?: LectureContentFetchOptions
): LectureThunk<
  FlashcardsState | (SuspendedContentIds & { flashcards: FlashcardsState })
> {
  return async (dispatch) => {
    try {
      const { flashcards, suspendedIds } = await postLectureFlashcards(
        lectureIds,
        options
      );

      dispatch(
        flashcardsLoaded({
          new: flashcards.new,
          due: flashcards.due,
          completed: flashcards.completed,
          syncToTodaySession: false,
        })
      );

      if (!options?.includeSuspended) return flashcards;

      return { flashcards, suspendedIds };
    } catch (error) {
      console.error('Error fetching lecture flashcards:', error);

      // Never resolve to an empty result on failure. A caller that turns this
      // list into a write payload (ManageContentModal's REPLACE) otherwise
      // cannot tell "this document has no flashcards" from "the request 500'd",
      // and would unlink every flashcard on the document (ORA-498 P4).
      // fetchLectureVignettes/fetchLectureVideos already rethrow.
      throw error;
    }
  };
}

/**
 * Fetch videos for multiple lectures in bulk and store them per lecture.
 * @param examId - Optional exam ID to fetch videos for all lectures in the exam
 * @param lectureIds - Optional array of specific lecture IDs to fetch videos for
 * @returns Object mapping lecture IDs to their video arrays
 */
export const fetchBulkLectureVideos =
  ({
    examId,
    lectureIds,
    page,
    limit,
  }: {
    examId?: string;
    lectureIds?: string[];
    page?: number;
    limit?: number;
  }): LectureThunk<Record<string, Video[]>> =>
  async (dispatch) => {
    if (lectureIds) {
      dispatch(bulkVideosLoading(lectureIds));
    }

    const videosByLecture = await postBulkLectureVideos({
      examId,
      lectureIds,
      page,
      limit,
    });

    // Single dispatch for all lectures and videos
    dispatch(bulkVideosLoaded(videosByLecture));

    return videosByLecture;
  };
